import { FilterList, KeyboardArrowDown } from "@mui/icons-material";
import {
    Box,
    Button,
    Card, CardContent,
    Collapse,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent, Stack, TextField, Typography
} from "@mui/material";
import React, { useState } from "react";
import {useParams} from "react-router-dom";
import {ProductCategoryFilter} from "../../types/Category";

interface CategoryFilterBarProps {
    onApplyFilter: (sku?: string, filter?: ProductCategoryFilter) => void;
    currentFilter?: ProductCategoryFilter;
}

const sortOptions = [
    { value: 'newest', label: 'Mas nuevo al mas viejo' },
    { value: 'oldest', label: 'Mas viejo al mas nuevo' },
    { value: 'price-asc', label: 'Precio: Menor a mayor' },
    { value: 'price-desc', label: 'Precio: Mayor a menor' },
    { value: 'a-z', label: 'A - Z' },
    { value: 'z-a', label: 'Z - A' }
];

const CategoryFilterBar: React.FC<CategoryFilterBarProps> = ({
                                                 onApplyFilter,
                                                 currentFilter
                                             }) => {
    const { sku } = useParams<{ sku: string }>();
    const [sortValue, setSortValue] = useState('newest');
    const [showFilters, setShowFilters] = useState(false);
    const [priceFrom, setPriceFrom] = useState('');
    const [priceTo, setPriceTo] = useState('');

    const handleSortChange = (event: SelectChangeEvent) => {
        const newValue = event.target.value;
        setSortValue(newValue);
        handleApplyFilter(newValue);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };
    
    const handleApplyFilter = (sort: string) => {
        const newFilter: ProductCategoryFilter = {
            pagina: 1,
            tamanoPagina: currentFilter?.tamanoPagina || 20,
            precioDesde: Number(priceFrom),
            precioHasta: Number(priceTo),
            orden: sort
        }
        if (sku) onApplyFilter(sku, newFilter)
        else onApplyFilter(undefined, newFilter)
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: 2,
                    py: 0.1,
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={toggleFilters}
                        sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#010101",
                            borderColor: "#e0e0e0",
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 2,
                            py: 1,
                            '&:hover': {
                                borderColor: '#9e9e9e',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        Filtrar
                    </Button>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            value={sortValue}
                            onChange={handleSortChange}
                            displayEmpty
                            IconComponent={KeyboardArrowDown}
                            sx={{
                                fontFamily: "'Rubik', sans-serif",
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#010101",
                                '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#9e9e9e',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2',
                                }
                            }}
                        >
                            {sortOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    sx={{
                                        fontFamily: "'Rubik', sans-serif",
                                        fontSize: "12px",
                                        fontWeight: 300
                                    }}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Collapse in={showFilters}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 0,
                        border: 'none',
                        borderTop: '1px solid #e0e0e0'
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontFamily: "'Rubik', sans-serif",
                                    fontSize: "16px",
                                    fontWeight: 700
                                }}
                            >
                                PRECIO
                            </Typography>
                            <Stack spacing={2} direction="row" alignItems="center">
                                <Stack>
                                    <Typography fontSize={9} fontWeight={500}>DESDE</Typography>
                                    <TextField
                                        value={priceFrom}
                                        onChange={(e) => setPriceFrom(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 0,
                                                fontFamily: "'Rubik', sans-serif",
                                                fontSize: "12px",
                                                height: "40px",
                                                '& fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                                '&:hover fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                            },
                                        }}
                                    />
                                </Stack>
                                <Stack>
                                    <Typography fontSize={9} fontWeight={500}>HASTA</Typography>
                                    <TextField
                                        value={priceTo}
                                        onChange={(e) => setPriceTo(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 0,
                                                fontFamily: "'Rubik', sans-serif",
                                                fontSize: "12px",
                                                height: "40px",
                                                '& fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                                '&:hover fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: '1px solid #e0e0e0',
                                                },
                                            },
                                        }}
                                    />
                                </Stack>
                                <Box sx={{paddingTop: 1.5}}>
                                    <Button variant='text' sx={{fontFamily: "'Rubik', sans-serif", color: 'black', fontWeight: 500 }} onClick={() => handleApplyFilter(sortValue)}>
                                        Aplicar
                                    </Button>
                                </Box>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Collapse>
        </Box>
    );
};


export default CategoryFilterBar;














